import type * as React from 'react';
import { t } from '@reos/i18n';
import { PREPARING_SECTIONS } from '../lib/texts.js';

/**
 * `/co-chystame` (krok UXA1) — jedno misto pro vsechno, co jeste nejede.
 *
 * Do tohoto kroku mely nehotove sekce bud stitek „Obchod brzy" roztrouseny
 * po menu, nebo vlastni polozku schovanou za přepínačem pilotu — R.B. 1. 10.
 * chce ani jedno: jen cisty seznam, dostupny z Nastaveni (avatar menu).
 *
 * Zadne polozky seznamu nejsou klikaci odkazy: vetsina tech cest se dnes
 * (pilot.showHiddenSections = false) presmeruje zpet na Dnesek
 * (`isPreparingPathHidden`, nav.ts) — odkaz, ktery jen odrazi, by byl horsi
 * nez zadny odkaz.
 *
 * ---------------------------------------------------------------------------
 * Krok FUNK1 (R.B. 4. 10. 2026 08:15, „pilot jede dnes") — SEDE
 * „PŘIPRAVUJEME" MISTO HOLE ODRAZKY.
 *
 * Zadani: „Nedokončené moduly nechat viditelné jako šedé »Připravujeme«
 * (neklikatelné nebo s jednou větou co přijde) … Nic nemaž." Seznam proto
 * nese u kazde polozky STITEK a JEDNU VETU, co prijde — veta uz v katalogu
 * je (`perex` sekce), jen ji stranka nevypisovala.
 *
 * MODULY BEZ OBRAZOVKY (`EXTRA_KEYS`) jsou tu jmenovite na zadani R.B.:
 * Asistentka uz v katalogu je jako „Schránka" (`/schranka`), ostatni tri
 * zatim nemaji ani cestu, ani polozku menu — a mit je nemaji: routa, ktera
 * jen odrazi zpet na Dnesek, je horsi nez poctiva veta „připravujeme".
 * Proto stoji v katalogu `webAdmin.comingSoon.extra.*`, ne v `nav.ts`.
 * ---------------------------------------------------------------------------
 */
const cs = { locale: 'cs' } as const;

interface Polozka {
  readonly key: string;
  readonly title: string;
  readonly perex: string;
}

const ZE_SEKCI: readonly Polozka[] = Object.entries(PREPARING_SECTIONS).map(([path, copy]) => ({
  key: path,
  title: copy.title,
  perex: copy.perex,
}));

/**
 * Moduly, ktere jeste nemaji ani obrazovku (zadani FUNK1 z 08:15).
 *
 * KDE JINDE ZIJE TENTYZ KONCEPT (krok MRGF1): tytez tri moduly ma jako
 * `PREPARING` i katalog verejneho webu `apps/web-public/src/lib/features.ts`
 * (`naborNaMiste`, `inzeratJednimKlikem`, `mobilniAplikace`) — tedy druha
 * stranka, ktera o nich rika „Připravujeme" (`/funkce`, `/navod`, nápověda).
 * SJEDNOTIT JE DO JEDNE FUNKCE NELZE bez nove sdilene vrstvy: jsou to dve
 * aplikace a web-admin na web-public nezavisi (CLAUDE.md §1 bod 4, §7).
 * Misto toho je drzi pohromade GUARD `co-chystame.spec.tsx` („seznam modulu
 * bez obrazovky se nerozejde s katalogem webu"): kdyz web nejakou z nich
 * prepne na `DONE`, admin ji prestane slibovat taky — a brana to rekne.
 */
const EXTRA_KEYS = ['nabor-na-miste', 'inzerat-jednim-klikem', 'mobilni-aplikace'] as const;

/** Pro guard v `co-chystame.spec.tsx` — klice, ktere stranka slibuje navic. */
export const COMING_SOON_EXTRA_KEYS: readonly string[] = EXTRA_KEYS;

const BEZ_OBRAZOVKY: readonly Polozka[] = EXTRA_KEYS.map((key) => ({
  key,
  title: t(`webAdmin.comingSoon.extra.${key}.title` as never, cs),
  perex: t(`webAdmin.comingSoon.extra.${key}.perex` as never, cs),
}));

const ITEMS: readonly Polozka[] = [...ZE_SEKCI, ...BEZ_OBRAZOVKY];

export function CoChystamePage(): React.JSX.Element {
  return (
    <div className="p-6" data-testid="co-chystame-page">
      <h1 className="mb-1 text-xl font-bold">{t('webAdmin.comingSoon.title', cs)}</h1>
      <p className="mb-4 max-w-2xl text-sm text-gray-600">{t('webAdmin.comingSoon.perex', cs)}</p>
      <ul
        aria-label={t('webAdmin.comingSoon.listLabel', cs)}
        className="m-0 grid list-none gap-2 p-0"
        data-testid="co-chystame-list"
      >
        {ITEMS.map((item) => (
          <li
            key={item.key}
            className="rounded border p-3"
            style={{ borderColor: 'var(--rh-border)', color: 'var(--rh-text-muted)' }}
            data-testid={`co-chystame-item-${item.key.replace(/^\//, '')}`}
          >
            <span className="rh-badge rh-badge--neutral mr-2">{t('webAdmin.comingSoon.badge', cs)}</span>
            <span className="font-medium" style={{ color: 'var(--rh-text)' }}>
              {item.title}
            </span>
            <span className="mt-1 block text-sm">{item.perex}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
